/** Page layout primitives shared by every console screen. */
import { cn } from '@uatp/ui';
import type { ReactNode } from 'react';

/** Standard page container (max 1400px, 24px padding). `wide` removes the max width (editors, simulation). */
export function Page({ children, className, wide }: { children: ReactNode; className?: string; wide?: boolean }) {
  return (
    <div className={cn('mx-auto flex min-h-full w-full flex-col gap-5 px-6 pb-12 pt-6', wide ? 'max-w-none' : 'max-w-[1400px]', className)}>
      {children}
    </div>
  );
}

/** Page title row: h1 + subtitle on the left, actions on the right (wraps on narrow screens). */
export function PageHeader({
  title,
  subtitle,
  actions,
  children,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-end justify-between gap-4', className)}>
      <div className="min-w-0">
        <h1 className="text-h1">{title}</h1>
        {subtitle && <div className="mt-0.5 text-13 text-text-2">{subtitle}</div>}
        {children}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Two-column label/value list used in detail panels. */
export function DefinitionList({ items, className }: { items: [ReactNode, ReactNode][]; className?: string }) {
  return (
    <dl className={cn('grid grid-cols-[minmax(110px,max-content)_1fr] gap-x-4 gap-y-2 text-13', className)}>
      {items.map(([k, v], i) => (
        <div key={i} className="contents">
          <dt className="text-text-2">{k}</dt>
          <dd className="m-0 min-w-0 break-words">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Card with a bordered header row (title + actions) and flush list content (dashboard lists, inbox panes). */
export function ListCard({
  title,
  actions,
  children,
  className,
  headingLevel = 2,
}: {
  title: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  headingLevel?: 2 | 3;
}) {
  const H = headingLevel === 2 ? 'h2' : 'h3';
  return (
    <section className={cn('min-w-0 overflow-hidden rounded-md border border-border bg-surface', className)}>
      <div className="flex items-center justify-between gap-2 border-b border-border-2 px-4 py-3">
        <H className="m-0 flex items-center gap-2 text-14 font-semibold">{title}</H>
        {actions && <div className="flex items-center gap-2 text-12">{actions}</div>}
      </div>
      <div className="[&>*:first-child]:border-t-0">{children}</div>
    </section>
  );
}

/** Padded card with a plain (sentence-case) title — charts and summaries. */
export function TitledCard({
  title,
  actions,
  children,
  className,
  headingLevel = 2,
}: {
  title: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  headingLevel?: 2 | 3;
}) {
  const H = headingLevel === 2 ? 'h2' : 'h3';
  return (
    <section className={cn('flex min-w-0 flex-col gap-3 rounded-md border border-border bg-surface p-4', className)}>
      <div className="flex items-center justify-between gap-2">
        <H className="m-0 text-14 font-semibold">{title}</H>
        {actions && <div className="flex items-center gap-2 text-12">{actions}</div>}
      </div>
      {children}
    </section>
  );
}
